import type { ResultOf } from "@opensea/graphql"
import type { AcceptOffersSummaryFragment } from "./AcceptOffersSummary.fragment"

type AcceptOffersSummaryData = ResultOf<typeof AcceptOffersSummaryFragment>
type ItemQuote = AcceptOffersSummaryData["itemQuotes"][0] & {
  __typename: "ItemQuote"
}
type Collection = NonNullable<ItemQuote["item"]["collection"]>

type CollectionGroup = {
  collection: Collection
  items: ItemQuote[]
  isCreatorFeesEnforced: boolean
  creatorFeeBasisPoints: number
}

export type CreatorFeeBreakdown = {
  collectionName: string
  feeBasisPoints: number
  fee: number
  feeUsd: number
}

type CollectionFeesResult = {
  optionalCreatorFees: CreatorFeeBreakdown[]
  requiredCreatorFees: CreatorFeeBreakdown[]
  totalOptionalFee: number
  totalOptionalFeeUsd: number
  totalRequiredFee: number
  totalRequiredFeeUsd: number
}

/**
 * Calculates the proportion of a collection's items relative to the total quantity
 */
export const calculateCollectionProportion = (
  group: CollectionGroup,
  totalQuantity: number,
): number => {
  const collectionQuantity = group.items.reduce(
    (sum, item) => sum + (item.quantity || 1),
    0,
  )
  return collectionQuantity / totalQuantity
}

/**
 * Calculates creator fee data for a specific collection
 */
export const calculateCollectionFeeData = (
  group: CollectionGroup,
  proportion: number,
  total: { unit: number },
  usd: number,
): CreatorFeeBreakdown => {
  const collectionFee =
    total.unit * proportion * (group.creatorFeeBasisPoints / 10_000)
  const collectionFeeUsd =
    usd * proportion * (group.creatorFeeBasisPoints / 10_000)

  return {
    collectionName: group.collection.name || "Unknown Collection",
    feeBasisPoints: group.creatorFeeBasisPoints,
    fee: collectionFee,
    feeUsd: collectionFeeUsd,
  }
}

/**
 * Categorizes collection fees into optional and required, calculating totals
 */
export const categorizeCollectionFees = (
  collections: CollectionGroup[],
  total: { unit: number },
  usd: number,
  totalQuantity: number,
  applyCreatorFee: boolean,
): CollectionFeesResult => {
  const collectionsWithFees = collections.filter(
    group => group.creatorFeeBasisPoints > 0,
  )

  const feeBreakdowns = collectionsWithFees.map(group => {
    const proportion = calculateCollectionProportion(group, totalQuantity)
    const feeData = calculateCollectionFeeData(group, proportion, total, usd)
    return { ...feeData, isEnforced: group.isCreatorFeesEnforced }
  })

  const optionalCreatorFees = feeBreakdowns
    .filter(fee => !fee.isEnforced)
    .map(({ isEnforced, ...fee }) => fee)

  const requiredCreatorFees = feeBreakdowns
    .filter(fee => fee.isEnforced)
    .map(({ isEnforced, ...fee }) => fee)

  const totalOptionalFee = applyCreatorFee
    ? optionalCreatorFees.reduce((sum, fee) => sum + fee.fee, 0)
    : 0

  const totalOptionalFeeUsd = applyCreatorFee
    ? optionalCreatorFees.reduce((sum, fee) => sum + fee.feeUsd, 0)
    : 0

  const totalRequiredFee = requiredCreatorFees.reduce(
    (sum, fee) => sum + fee.fee,
    0,
  )
  const totalRequiredFeeUsd = requiredCreatorFees.reduce(
    (sum, fee) => sum + fee.feeUsd,
    0,
  )

  return {
    optionalCreatorFees,
    requiredCreatorFees,
    totalOptionalFee,
    totalOptionalFeeUsd,
    totalRequiredFee,
    totalRequiredFeeUsd,
  }
}

/**
 * Groups item quotes by their collection
 */
export const groupItemQuotesByCollection = (
  itemQuotes: AcceptOffersSummaryData["itemQuotes"],
): CollectionGroup[] => {
  const collectionGroups: Record<string, CollectionGroup> = {}

  for (const itemQuote of itemQuotes) {
    if (itemQuote.__typename === "ItemQuote" && itemQuote.item.collection) {
      const collection = itemQuote.item.collection
      const collectionId = collection.id || collection.name || "unknown"

      if (!collectionGroups[collectionId]) {
        collectionGroups[collectionId] = {
          collection,
          items: [],
          isCreatorFeesEnforced:
            (collection.flags?.isCreatorFeesEnforced ?? false) ||
            (collection.fees?.totalCreatorFee?.isRequired ?? false),
          creatorFeeBasisPoints:
            collection.fees?.totalCreatorFee?.feeBasisPoints ?? 0,
        }
      }

      collectionGroups[collectionId].items.push(itemQuote as ItemQuote)
    }
  }

  return Object.values(collectionGroups)
}

/**
 * Analyzes creator fee states across collections
 */
export const analyzeCreatorFeeStates = (collections: CollectionGroup[]) => {
  return {
    allCreatorFeesEnforced: collections.every(
      group => group.isCreatorFeesEnforced,
    ),
    hasOptionalCreatorFees: collections.some(
      group => group.creatorFeeBasisPoints > 0 && !group.isCreatorFeesEnforced,
    ),
    hasRequiredCreatorFees: collections.some(
      group => group.creatorFeeBasisPoints > 0 && group.isCreatorFeesEnforced,
    ),
  }
}

/**
 * Determines whether to apply creator fees based on user preference
 */
export const shouldApplyOptionalCreatorFees = (
  applyCreatorFee?: boolean,
): boolean => {
  return applyCreatorFee ?? false
}

/**
 * Calculates the total quantity across all item quotes
 */
export const calculateTotalQuantity = (
  itemQuotes: AcceptOffersSummaryData["itemQuotes"],
): number => {
  return itemQuotes.reduce(
    (sum, itemQuote) =>
      sum +
      (itemQuote.__typename === "ItemQuote" ? itemQuote.quantity || 1 : 1),
    0,
  )
}
